import { useEffect, useState } from "react";
import { ApiClientError, fetchLine } from "../api/client";
import { StateBanner } from "../components/StateBanner";
import { PeriodSwitcher } from "../components/PeriodSwitcher";
import { BarChart } from "../components/BarChart";
import { yen } from "../lib/meta";
import { resolveRange, type Period } from "../lib/dateRange";
import type {
  LineAccount,
  LineFunnel,
  LineResponse,
  LineRsnsDemographics,
  LineRsnsMetrics,
} from "../types/contract";

type FetchState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ok"; data: LineResponse };

const FUNNEL_STEPS: Array<{ key: keyof LineFunnel; label: string }> = [
  { key: "Lead", label: "Lead" },
  { key: "EngagedLead", label: "EngagedLead" },
  { key: "QualifiedLead", label: "QualifiedLead" },
  { key: "HighIntentLead", label: "HighIntentLead" },
  { key: "OwnerRegistered", label: "OwnerRegistered" },
];

function formatMD(dateStr: string): string {
  const [, m, d] = dateStr.split("-");
  return `${Number(m)}/${Number(d)}`;
}

function accentColor(key: string): string {
  return key === "rakuten" ? "var(--ch-rakuten-c)" : "var(--ch-line-c)";
}

// rsns.metrics のキーは取込CSVのヘッダ名そのまま（number|string混在）。
// R-SNSレポートを表示するアカウント固定値（現状の運用は楽天=R-SNSのみ・自社は対象外）。
const RSNS_ACCOUNT_KEY = "rakuten";
// shopSales（Shopify注文のLINE経由アトリビューション）を表示するアカウント固定値
// （現状の運用は自社=Shopifyのみ・楽天は対象外）。
const SHOP_SALES_ACCOUNT_KEY = "own";

// trend_overall / trend_mestype 共通の主要指標（実CSVで確定した列名）。
const RSNS_METRIC_COLUMNS = ["訪問者数", "転換率", "売上", "転換数"] as const;

function formatMetricValue(v: number | string | undefined): string {
  if (v === undefined) return "—";
  if (typeof v === "number") return v.toLocaleString("ja-JP");
  return v;
}

function formatMonthLabel(month: string): string {
  const [y, m] = month.split("-");
  return `${y}/${Number(m)}`;
}

function formatRoas(v: number | null): string {
  return v !== null ? `${v.toFixed(2)}x` : "—";
}

function formatYenOrDash(v: number | null): string {
  return v !== null ? yen(v) : "—";
}

/** 属性別テーブルの「値のある行」判定。null/0/未計測は非表示対象（全ゼロ行は隠す）。 */
function hasPositiveValue(v: number | null): boolean {
  return typeof v === "number" && v > 0;
}

function DemographicsSection({ demographics }: { demographics: LineRsnsDemographics }) {
  const groupsWithVisibleRows = demographics.groups.map((g) => ({
    name: g.name,
    visibleRows: g.rows.filter((r) => hasPositiveValue(r.visitors) || hasPositiveValue(r.buyers)),
  }));
  const anyVisible = groupsWithVisibleRows.some((g) => g.visibleRows.length > 0);

  return (
    <>
      <h3 className="section-title" style={{ marginTop: 16 }}>
        属性別（月間ユニーク・最新月）
        <span className="rsns-caption"> （{formatMonthLabel(demographics.month)}）</span>
      </h3>
      {!anyVisible ? (
        <div className="empty-state">まだ購入者・訪問者データがありません（数値が出ると自動表示）</div>
      ) : (
        groupsWithVisibleRows.map((g) => (
          <div key={g.name} style={{ marginBottom: 12 }}>
            <div className="rsns-subcaption">{g.name}</div>
            {g.visibleRows.length === 0 ? (
              <div className="empty-state">—</div>
            ) : (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>属性</th>
                      <th>訪問者数</th>
                      <th>購入者数</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.visibleRows.map((r) => (
                      <tr key={r.label}>
                        <td>{r.label}</td>
                        <td>{r.visitors !== null ? r.visitors.toLocaleString("ja-JP") : "—"}</td>
                        <td>{r.buyers !== null ? r.buyers.toLocaleString("ja-JP") : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))
      )}
    </>
  );
}

function RsnsSection({ rsns }: { rsns: NonNullable<LineAccount["rsns"]> }) {
  return (
    <>
      <h3 className="section-title" style={{ marginTop: 16 }}>
        R-SNSレポート
        {rsns.latestMonth && (
          <span className="rsns-caption"> （最新: {formatMonthLabel(rsns.latestMonth)}）</span>
        )}
      </h3>

      {rsns.trend.length === 0 ? (
        <div className="empty-state">月次サマリ（trend_overall）は未取込です。</div>
      ) : (
        <>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>月</th>
                  {RSNS_METRIC_COLUMNS.map((c) => (
                    <th key={c}>{c}</th>
                  ))}
                  <th>広告費</th>
                  <th>ROAS</th>
                  <th>CPA</th>
                </tr>
              </thead>
              <tbody>
                {rsns.trend.map((t) => (
                  <tr key={t.month}>
                    <td>{formatMonthLabel(t.month)}</td>
                    {RSNS_METRIC_COLUMNS.map((c) => (
                      <td key={c}>{formatMetricValue((t.metrics as LineRsnsMetrics)[c])}</td>
                    ))}
                    <td>{formatYenOrDash(t.adSpend)}</td>
                    <td>{formatRoas(t.roas)}</td>
                    <td>{formatYenOrDash(t.cpa)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="rsns-subcaption">※売上はR-SNS計測のLINE経由売上（広告以外の友だち経由も含む月次値）</div>
        </>
      )}

      {rsns.latestMestypeBreakdown.length > 0 && (
        <>
          <div className="rsns-subcaption">最新月の種別内訳（配信/リッチメニュー経由の内訳）</div>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>種別</th>
                  <th>訪問者数</th>
                  <th>売上</th>
                </tr>
              </thead>
              <tbody>
                {rsns.latestMestypeBreakdown.map((r) => (
                  <tr key={r.label}>
                    <td>{r.label}</td>
                    <td>{formatMetricValue((r.metrics as LineRsnsMetrics)["訪問者数"])}</td>
                    <td>{formatMetricValue((r.metrics as LineRsnsMetrics)["売上"])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {rsns.demographics && <DemographicsSection demographics={rsns.demographics} />}
    </>
  );
}

export function LineAnalyticsPage() {
  const [period, setPeriod] = useState<Period>("30d");
  const [customStart, setCustomStart] = useState("2026-07-19");
  const [customEnd, setCustomEnd] = useState("2026-08-18");
  const [state, setState] = useState<FetchState>({ status: "loading" });

  const range = resolveRange(period, { start: customStart, end: customEnd });

  const load = () => {
    setState({ status: "loading" });
    fetchLine(range.from, range.to)
      .then((data) => setState({ status: "ok", data }))
      .catch((e: unknown) => {
        const message = e instanceof ApiClientError ? e.message : "予期しないエラーが発生しました。";
        setState({ status: "error", message });
      });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, customStart, customEnd]);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">LINE分析</h1>
        <PeriodSwitcher
          period={period}
          onChange={setPeriod}
          customStart={customStart}
          customEnd={customEnd}
          onCustomStartChange={setCustomStart}
          onCustomEndChange={setCustomEnd}
        />
      </div>

      {state.status === "loading" && <SkeletonAccounts />}
      {state.status === "error" && <StateBanner kind="error" message={state.message} onRetry={load} />}

      {state.status === "ok" && (
        <div className="line-accounts-grid">
          {state.data.accounts.map((acc) => (
            <LineAccountCard key={acc.key} acc={acc} />
          ))}
        </div>
      )}
    </div>
  );
}

function LineAccountCard({ acc }: { acc: LineAccount }) {
  const color = accentColor(acc.key);

  return (
    <div className="card line-account-card">
      <h2 className="section-title">{acc.label}</h2>

      {acc.missing && <StateBanner kind="missing" message={`${acc.label} のデータ取得に失敗しました（一部欠損）。`} />}
      {acc.note && <div className="line-account-note">※{acc.note}</div>}

      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-label">広告費</div>
          <div className="kpi-value">{acc.spend !== null ? yen(acc.spend) : "—"}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Meta Lead</div>
          <div className="kpi-value">{acc.metaLeads !== null ? `${acc.metaLeads.toLocaleString("ja-JP")}件` : "—"}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">CPF</div>
          <div className="kpi-value">{acc.cpf !== null ? yen(acc.cpf) : "—"}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">クリック→友だちマッチ率</div>
          <div className="kpi-value">{acc.matchRate !== null ? `${acc.matchRate.toFixed(1)}%` : "—"}</div>
          <div className="kpi-sub">
            {acc.clicks.toLocaleString("ja-JP")}クリック / {acc.matched.toLocaleString("ja-JP")}マッチ
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">オーナー登録</div>
          <div className="kpi-value">
            {acc.ownerRegistrations ? `${acc.ownerRegistrations.count.toLocaleString("ja-JP")}件` : "—"}
          </div>
          {acc.ownerRegistrations && (
            <div className="kpi-sub">
              うちLIFF{acc.ownerRegistrations.liffCount.toLocaleString("ja-JP")}件
              {acc.ownerRegistrations.breakdown.length > 1 && (
                <>
                  {" "}
                  （
                  {acc.ownerRegistrations.breakdown
                    .map((b) => `${b.label}${b.count.toLocaleString("ja-JP")}`)
                    .join(" / ")}
                  ）
                </>
              )}
            </div>
          )}
        </div>
        {acc.funnelMode === "lead_only" && (
          <div className="kpi-card">
            <div className="kpi-label">CAPI Lead送信</div>
            <div className="kpi-value">{acc.funnel.Lead.toLocaleString("ja-JP")}件</div>
          </div>
        )}
        {acc.key === RSNS_ACCOUNT_KEY && (
          <>
            <div className="kpi-card">
              <div className="kpi-label">LINE経由売上（当月）</div>
              <div className="kpi-value">{formatYenOrDash(acc.rsns?.latest?.sales ?? null)}</div>
            </div>
            <div className="kpi-card">
              <div className="kpi-label">ROAS（当月・月次）</div>
              <div className="kpi-value">{formatRoas(acc.rsns?.latest?.roas ?? null)}</div>
            </div>
            <div className="kpi-card">
              <div className="kpi-label">CPA（当月・月次）</div>
              <div className="kpi-value">{formatYenOrDash(acc.rsns?.latest?.cpa ?? null)}</div>
            </div>
          </>
        )}
        {acc.key === SHOP_SALES_ACCOUNT_KEY && (
          <>
            <div className="kpi-card">
              <div className="kpi-label">LINE経由売上</div>
              <div className="kpi-value">{formatYenOrDash(acc.shopSales?.lineAttributed.sales ?? null)}</div>
              {acc.shopSales && (
                <div className="kpi-sub">{acc.shopSales.lineAttributed.orders.toLocaleString("ja-JP")}件</div>
              )}
            </div>
            <div className="kpi-card">
              <div className="kpi-label">ROAS</div>
              <div className="kpi-value">{formatRoas(acc.shopSales?.roas ?? null)}</div>
            </div>
            <div className="kpi-card">
              <div className="kpi-label">CPA</div>
              <div className="kpi-value">{formatYenOrDash(acc.shopSales?.cpa ?? null)}</div>
            </div>
          </>
        )}
      </div>

      {acc.key === SHOP_SALES_ACCOUNT_KEY && !acc.shopSales && (
        <div className="rsns-subcaption">LINE経由売上: データなし（utm付与運用の開始後に表示されます）</div>
      )}
      {acc.key === SHOP_SALES_ACCOUNT_KEY && acc.shopSales && (
        <div className="rsns-subcaption">※{acc.shopSales.note}</div>
      )}
      {acc.ownerRegistrations && <div className="rsns-subcaption">※{acc.ownerRegistrations.note}</div>}

      {acc.funnelMode !== "lead_only" && (
        <>
          <h3 className="section-title" style={{ marginTop: 16 }}>
            CAPIファネル
          </h3>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  {FUNNEL_STEPS.map((s) => (
                    <th key={s.key}>{s.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {FUNNEL_STEPS.map((s) => (
                    <td key={s.key}>{acc.funnel[s.key].toLocaleString("ja-JP")}</td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}

      <h3 className="section-title" style={{ marginTop: 16 }}>
        日別クリック/マッチ
      </h3>
      {acc.daily.length === 0 ? (
        <div className="empty-state">データがありません。</div>
      ) : (
        <BarChart
          data={acc.daily.map((d) => ({ label: formatMD(d.date), value: d.clicks }))}
          series2={acc.daily.map((d) => ({ label: formatMD(d.date), value: d.matched }))}
          color={color}
          color2="var(--accent)"
          legend={[
            { label: "クリック", color },
            { label: "友だちマッチ", color: "var(--accent)" },
          ]}
        />
      )}

      <h3 className="section-title" style={{ marginTop: 16 }}>
        流入経路
      </h3>
      {acc.sources.length === 0 ? (
        <div className="empty-state">流入経路データがありません。</div>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>source</th>
                <th>medium</th>
                <th>campaign</th>
                <th>content</th>
                <th>クリック</th>
                <th>マッチ</th>
                <th>Lead</th>
              </tr>
            </thead>
            <tbody>
              {acc.sources.map((s, i) => (
                <tr key={`${s.source}|${s.medium}|${s.campaign}|${s.content}|${i}`}>
                  <td>{s.source}</td>
                  <td>{s.medium}</td>
                  <td>{s.campaign}</td>
                  <td>{s.content}</td>
                  <td>{s.clicks.toLocaleString("ja-JP")}</td>
                  <td>{s.matched.toLocaleString("ja-JP")}</td>
                  <td>{s.leads.toLocaleString("ja-JP")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {acc.campaigns.length > 0 && (
        <>
          <h3 className="section-title" style={{ marginTop: 16 }}>
            対象キャンペーン
          </h3>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>キャンペーン名</th>
                  <th>広告費</th>
                </tr>
              </thead>
              <tbody>
                {acc.campaigns.map((c) => (
                  <tr key={c.id}>
                    <td style={{ whiteSpace: "normal", minWidth: 180, wordBreak: "break-all" }}>{c.name}</td>
                    <td>{yen(c.spend)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {acc.key === RSNS_ACCOUNT_KEY &&
        (acc.rsns === null ? (
          <>
            <h3 className="section-title" style={{ marginTop: 16 }}>
              R-SNSレポート
            </h3>
            <div className="empty-state">未取込</div>
          </>
        ) : (
          <RsnsSection rsns={acc.rsns} />
        ))}
    </div>
  );
}

function SkeletonAccounts() {
  return (
    <div className="line-accounts-grid">
      {[0, 1].map((i) => (
        <div className="card" key={i}>
          <div className="skeleton" style={{ height: 16, width: "40%", marginBottom: 14 }} />
          <div className="kpi-grid">
            {[0, 1, 2, 3].map((j) => (
              <div className="kpi-card" key={j}>
                <div className="skeleton" style={{ height: 12, width: "60%", marginBottom: 10 }} />
                <div className="skeleton" style={{ height: 22, width: "80%" }} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
